/**
 * @fileoverview Busca dentro da conversa do Chat Interno, como no WhatsApp: a
 * caixa aparece no canto direito, abaixo da lupa do cabeçalho, mostra em qual
 * ocorrência está e quantas são ("1 de 5"), grifa o termo na conversa e anda
 * entre as ocorrências, da mais recente para a mais antiga.
 *
 * O servidor diz em quais mensagens o termo aparece e quantas vezes, e devolve
 * a expressão do termo; a tela grifa com ela (searchHighlight.js). O grifo usa
 * a CSS Custom Highlight API: marca o texto sem mexer no que o React desenhou e
 * sem redesenhar a lista. A barra guarda o próprio texto digitado, então
 * digitar na busca também não redesenha as mensagens.
 */

import React, {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { makeStyles } from "@material-ui/core/styles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Tooltip from "@material-ui/core/Tooltip";
import { ChevronDown, ChevronUp, Search, X } from "lucide-react";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { useDebounce } from "../../hooks/useDebounce";
import { buildRanges } from "./searchHighlight";

const HIGHLIGHT_ALL = "ic-search";
const HIGHLIGHT_CURRENT = "ic-search-current";
const GLOW_CLASS = "ic-search-glow";
const MIN_TERM_LENGTH = 2;
const TYPING_DELAY_MS = 250;
// Mensagem nova, editada ou apagada na conversa: refaz a busca depois deste tempo.
const REFRESH_DELAY_MS = 600;
// Andando rápido entre ocorrências de trechos não carregados, só a última é buscada.
const JUMP_DELAY_MS = 60;
const LEAVE_MS = 130;
const GLOW_MS = 1300;

const supportsHighlights = () =>
  typeof window !== "undefined" && !!window.CSS && !!window.CSS.highlights && typeof window.Highlight === "function";

const prefersReducedMotion = () =>
  typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const clearHighlights = () => {
  if (!supportsHighlights()) return;
  window.CSS.highlights.delete(HIGHLIGHT_ALL);
  window.CSS.highlights.delete(HIGHLIGHT_CURRENT);
};

const EMPTY = { results: [], offsets: [], hits: new Map(), pattern: null };

/**
 * A ocorrência de número `index` (0 = a mais recente): mensagem e posição
 * dentro dela, contada do começo do texto. Dentro da mesma mensagem a ordem é
 * de baixo para cima, para a seta "mais antiga" sempre subir na tela.
 */
const locate = ({ results, offsets }, index) => {
  let low = 0;
  let high = results.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (offsets[middle] <= index) low = middle;
    else high = middle - 1;
  }
  const result = results[low];
  return result ? { id: result.id, nth: result.hits - 1 - (index - offsets[low]) } : null;
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  const accent = dark ? "#5B7CFF" : "#3D5CFD";
  const accentSoft = dark ? "rgba(91,124,255,0.12)" : "rgba(61,92,253,0.08)";
  const border = dark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)";
  const borderStrong = dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const text0 = dark ? "#FAFAFA" : "#09090B";
  const muted = dark ? "#A1A1AA" : "#52525B";

  return {
    "@global": {
      // O marca-texto: amarelo para as ocorrências, mais forte na atual. O azul
      // já é das menções.
      [`::highlight(${HIGHLIGHT_ALL})`]: {
        backgroundColor: dark ? "rgba(250,204,21,0.30)" : "#FDE68A",
      },
      [`::highlight(${HIGHLIGHT_CURRENT})`]: {
        backgroundColor: dark ? "#FACC15" : "#F59E0B",
        color: "#0A0A0A",
      },
      "@keyframes icSearchGlow": {
        from: { backgroundColor: dark ? "rgba(250,204,21,0.10)" : "rgba(245,158,11,0.16)" },
        to: { backgroundColor: "transparent" },
      },
      [`.${GLOW_CLASS}`]: { animation: "icSearchGlow 1.2s ease-out" },
      "@keyframes icSearchEnter": {
        from: { opacity: 0, transform: "translateY(-6px)" },
        to: { opacity: 1, transform: "translateY(0)" },
      },
      "@keyframes icSearchLeave": {
        from: { opacity: 1, transform: "translateY(0)" },
        to: { opacity: 0, transform: "translateY(-6px)" },
      },
      "@keyframes icSearchProgress": {
        from: { transform: "translateX(-100%)" },
        to: { transform: "translateX(400%)" },
      },
    },
    // Caixa no canto direito, logo abaixo da lupa do cabeçalho (o mouse já está
    // ali), por cima das mensagens: as ações de hover delas ficam em 10. Fundo
    // sólido, senão o texto de trás vazava.
    bar: {
      position: "absolute",
      top: 8,
      right: 24,
      zIndex: 12,
      width: 480,
      maxWidth: "calc(100% - 48px)",
      display: "flex",
      alignItems: "center",
      gap: 2,
      padding: 6,
      overflow: "hidden",
      background: dark ? "#18181B" : "#ffffff",
      border: `1px solid ${borderStrong}`,
      borderRadius: 12,
      boxShadow: dark ? "0 8px 24px rgba(0,0,0,0.45)" : "0 8px 24px rgba(15,23,42,0.10)",
      animation: "icSearchEnter 160ms cubic-bezier(0.2, 0, 0, 1)",
      [theme.breakpoints.down("sm")]: { right: 12, maxWidth: "calc(100% - 24px)" },
    },
    barLeaving: { animation: "icSearchLeave 120ms ease-in forwards" },
    barStill: { animation: "none" },
    field: {
      position: "relative",
      flex: 1,
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      marginRight: 4,
    },
    fieldIcon: { position: "absolute", left: 11, color: muted, pointerEvents: "none" },
    input: {
      width: "100%",
      height: 36,
      padding: "0 112px 0 34px",
      fontSize: 13.5,
      fontFamily: "inherit",
      color: text0,
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(0,0,0,0.03)",
      border: `1px solid ${border}`,
      borderRadius: 10,
      outline: "none",
      transition: "border-color 0.15s, box-shadow 0.15s, background 0.15s",
      "&::placeholder": { color: muted, opacity: 1 },
      "&:hover": { borderColor: borderStrong },
      "&:focus": {
        borderColor: accent,
        boxShadow: `0 0 0 1px ${accentSoft}`,
        background: dark ? "rgba(255,255,255,0.06)" : "#ffffff",
      },
    },
    // Algarismos da mesma largura: o número não "pula" ao andar.
    counter: {
      position: "absolute",
      right: 12,
      display: "flex",
      alignItems: "center",
      gap: 4,
      fontSize: 12,
      color: muted,
      fontVariantNumeric: "tabular-nums",
      whiteSpace: "nowrap",
      pointerEvents: "none",
      transition: "opacity 0.15s",
    },
    counterStale: { opacity: 0.45 },
    counterNow: { color: text0, fontWeight: 600 },
    button: {
      width: 32,
      height: 32,
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 0,
      border: "none",
      borderRadius: 8,
      background: "transparent",
      color: muted,
      cursor: "pointer",
      transition: "color 0.15s, background 0.15s",
      "&:hover:not(:disabled)": {
        color: text0,
        background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)",
      },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${accent}` },
      "&:disabled": { opacity: 0.35, cursor: "default" },
    },
    divider: { width: 1, height: 18, margin: "0 4px", flexShrink: 0, background: borderStrong },
    // Fio de carregamento enquanto um trecho antigo é buscado.
    progress: {
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: 2,
      overflow: "hidden",
      pointerEvents: "none",
    },
    progressBar: {
      width: "25%",
      height: "100%",
      background: accent,
      animation: "icSearchProgress 900ms linear infinite",
    },
    progressStill: { width: "100%", opacity: 0.5, animation: "none" },
  };
});

const SearchBar = forwardRef(({ channelId, listRef, onJump, onClose, holdPaging, leaving }, ref) => {
  const classes = useStyles();
  const reduced = prefersReducedMotion();

  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  // O que o contador mostra: termo já buscado, total de ocorrências e se há mais além delas.
  const [shown, setShown] = useState({ term: "", total: 0, truncated: false });
  const [position, setPosition] = useState(0);
  const [jumping, setJumping] = useState(false);

  const inputRef = useRef(null);
  const barRef = useRef(null);
  const found = useRef(EMPTY); // resposta em uso, com as somas para achar a ocorrência nº N
  const requested = useRef(""); // último termo pedido ao servidor
  const current = useRef(null); // { id, nth }: a ocorrência atual
  const ranges = useRef(new Map()); // id da mensagem -> trechos grifados na tela
  const glowing = useRef(null);
  const searchSeq = useRef(0);
  const navSeq = useRef(0);
  const frame = useRef(0);
  const timers = useRef({ glow: null, refresh: null });

  const paint = useCallback(() => {
    if (!supportsHighlights()) return;
    const now = current.current;
    const others = new window.Highlight();
    let mine = null;
    ranges.current.forEach((list, id) =>
      list.forEach((range, nth) => {
        if (now && now.id === id && now.nth === nth) mine = range;
        else others.add(range);
      })
    );
    window.CSS.highlights.set(HIGHLIGHT_ALL, others);
    if (mine) {
      // A atual fica por cima das outras, não importa a ordem em que foram criadas.
      const highlight = new window.Highlight(mine);
      highlight.priority = 1;
      window.CSS.highlights.set(HIGHLIGHT_CURRENT, highlight);
    } else {
      window.CSS.highlights.delete(HIGHLIGHT_CURRENT);
    }
  }, []);

  // Refaz os trechos das mensagens que estão na tela e têm o termo. Só entram
  // as que o servidor devolveu, e no máximo as ocorrências que ele contou.
  const rebuild = useCallback(() => {
    const list = listRef.current;
    const { pattern, hits } = found.current;
    const next = new Map();
    if (list && pattern && hits.size) {
      let regex = null;
      try {
        regex = new RegExp(pattern, "gi");
      } catch (err) {
        regex = null;
      }
      if (regex) {
        list.querySelectorAll("[data-msg-text]").forEach((element) => {
          const id = Number(element.getAttribute("data-msg-text"));
          const max = hits.get(id);
          if (max) next.set(id, buildRanges(element, regex, max));
        });
      }
    }
    ranges.current = next;
    paint();
  }, [listRef, paint]);

  // Leva a ocorrência atual para a parte visível da conversa, abaixo da barra.
  const reveal = useCallback(
    (instant) => {
      const list = listRef.current;
      const now = current.current;
      if (!list || !now) return null;
      const row = document.getElementById(`msg-${now.id}`);
      const range = (ranges.current.get(now.id) || [])[now.nth];
      let rect = range ? range.getBoundingClientRect() : null;
      // Sem o trecho na tela (o termo atravessa uma marcação), vale a mensagem.
      if ((!rect || (!rect.width && !rect.height)) && row) rect = row.getBoundingClientRect();
      if (!rect) return row;

      const box = list.getBoundingClientRect();
      const bar = barRef.current;
      const top = box.top + (bar ? bar.offsetTop + bar.offsetHeight : 0) + 12;
      const bottom = box.bottom - 12;
      if (rect.top < top || rect.bottom > bottom) {
        const delta = rect.top + rect.height / 2 - (top + bottom) / 2;
        const smooth = !instant && !prefersReducedMotion() && Math.abs(delta) < list.clientHeight * 1.5;
        holdPaging(smooth ? 700 : 200);
        list.scrollTo({ top: list.scrollTop + delta, behavior: smooth ? "smooth" : "auto" });
      }
      return row;
    },
    [listRef, holdPaging]
  );

  const glow = useCallback((row) => {
    if (glowing.current) glowing.current.classList.remove(GLOW_CLASS);
    clearTimeout(timers.current.glow);
    glowing.current = null;
    if (!row || prefersReducedMotion()) return;
    row.classList.add(GLOW_CLASS);
    glowing.current = row;
    timers.current.glow = setTimeout(() => {
      row.classList.remove(GLOW_CLASS);
      if (glowing.current === row) glowing.current = null;
    }, GLOW_MS);
  }, []);

  const goTo = useCallback(
    async (index) => {
      const target = locate(found.current, index);
      if (!target) return;
      navSeq.current += 1;
      const nav = navSeq.current;
      const previous = current.current;
      current.current = target;
      setPosition(index);

      const loaded = !!document.getElementById(`msg-${target.id}`);
      if (loaded) {
        setJumping(false);
        paint();
      } else {
        setJumping(true);
        await wait(JUMP_DELAY_MS);
        if (nav !== navSeq.current) return;
        const ok = await onJump(target.id);
        // Outra ocorrência foi pedida no meio do caminho: vale a última.
        if (nav !== navSeq.current) return;
        setJumping(false);
        if (!ok) return;
        rebuild();
      }
      const row = reveal(!loaded);
      if (!previous || previous.id !== target.id) glow(row);
    },
    [onJump, paint, rebuild, reveal, glow]
  );

  const apply = useCallback(
    (payload, term, keepPlace) => {
      const results = (payload && payload.results) || [];
      const offsets = [];
      const hits = new Map();
      let total = 0;
      results.forEach((result) => {
        offsets.push(total);
        total += result.hits;
        hits.set(result.id, result.hits);
      });
      found.current = { results, offsets, hits, pattern: (payload && payload.pattern) || null };
      setShown({ term, total, truncated: !!(payload && payload.truncated) });

      if (!total) {
        current.current = null;
        navSeq.current += 1;
        setPosition(0);
        setJumping(false);
        rebuild();
        return;
      }
      if (keepPlace && current.current) {
        // Mesma ocorrência de antes; se a mensagem saiu da lista, a mais antiga seguinte.
        const at = results.findIndex((result) => result.id <= current.current.id);
        const i = at === -1 ? results.length - 1 : at;
        const same = results[i].id === current.current.id;
        const nth = same ? Math.min(current.current.nth, results[i].hits - 1) : results[i].hits - 1;
        current.current = { id: results[i].id, nth };
        setPosition(offsets[i] + results[i].hits - 1 - nth);
        rebuild();
        return;
      }
      rebuild();
      goTo(0);
    },
    [goTo, rebuild]
  );

  const runSearch = useCallback(
    async (text, keepPlace = false) => {
      const term = text.trim();
      requested.current = term;
      searchSeq.current += 1;
      const seq = searchSeq.current;
      if (!keepPlace) setFailed(false);
      if (Array.from(term).length < MIN_TERM_LENGTH) {
        setSearching(false);
        apply(null, term, false);
        return;
      }
      if (!keepPlace) setSearching(true);
      try {
        const { data } = await api.get(`/internal-chat/channels/${channelId}/messages/search`, {
          params: { q: term },
        });
        // Resposta de um termo que já não é o pedido: descarta.
        if (seq !== searchSeq.current) return;
        setSearching(false);
        apply(data, term, keepPlace);
      } catch (err) {
        if (seq !== searchSeq.current) return;
        setSearching(false);
        // A atualização silenciosa que falha não muda nada na tela. A busca
        // pedida pelo usuário avisa, e o termo deixa de valer como "já buscado":
        // Enter tenta de novo.
        if (keepPlace) return;
        requested.current = "";
        setFailed(true);
        toastError(err);
      }
    },
    [apply, channelId]
  );

  const typed = useDebounce(query, TYPING_DELAY_MS);
  useEffect(() => {
    if (typed.trim() !== requested.current) runSearch(typed);
  }, [typed, runSearch]);

  useImperativeHandle(
    ref,
    () => ({
      focus: () => {
        if (!inputRef.current) return;
        inputRef.current.focus({ preventScroll: true });
        inputRef.current.select();
      },
      refresh: () => {
        if (Array.from(requested.current).length < MIN_TERM_LENGTH) return;
        clearTimeout(timers.current.refresh);
        timers.current.refresh = setTimeout(() => runSearch(requested.current, true), REFRESH_DELAY_MS);
      },
    }),
    [runSearch]
  );

  // A caixa fica por cima do topo da lista: a lista ganha esse espaço no começo
  // e a rolagem compensa, então nada se mexe na tela e a primeira mensagem (e o
  // aviso de carregando) não ficam escondidos atrás dela.
  useLayoutEffect(() => {
    const list = listRef.current;
    const bar = barRef.current;
    if (!list || !bar) return undefined;
    const height = bar.offsetTop + bar.offsetHeight;
    const before = list.style.paddingTop;
    list.style.paddingTop = `${height + 8}px`;
    list.scrollTop += height;
    return () => {
      list.style.paddingTop = before;
      list.scrollTop -= height;
    };
  }, [listRef]);

  // Mensagens entrando, saindo ou mudando de texto na lista: o grifo acompanha.
  useEffect(() => {
    const list = listRef.current;
    if (!list || typeof MutationObserver === "undefined") return undefined;
    const observer = new MutationObserver(() => {
      if (frame.current) return;
      frame.current = requestAnimationFrame(() => {
        frame.current = 0;
        rebuild();
      });
    });
    observer.observe(list, { childList: true, subtree: true, characterData: true });
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame.current);
      frame.current = 0;
    };
  }, [listRef, rebuild]);

  useEffect(() => {
    if (inputRef.current) inputRef.current.focus({ preventScroll: true });
    const pending = timers.current;
    return () => {
      searchSeq.current += 1;
      navSeq.current += 1;
      clearTimeout(pending.glow);
      clearTimeout(pending.refresh);
      if (glowing.current) glowing.current.classList.remove(GLOW_CLASS);
      clearHighlights();
    };
  }, []);

  const { total, truncated } = shown;
  const term = query.trim();
  const longEnough = Array.from(term).length >= MIN_TERM_LENGTH;
  const waiting = searching || (!failed && term !== shown.term);

  const step = (delta) => {
    const next = position + delta;
    if (next >= 0 && next < total) goTo(next);
  };

  const handleKeyDown = (event) => {
    if (event.key === "Escape") {
      event.preventDefault();
      onClose();
      return;
    }
    const enter = event.key === "Enter";
    const older = (enter && !event.shiftKey) || event.key === "ArrowUp";
    const newer = (enter && event.shiftKey) || event.key === "ArrowDown";
    if ((!older && !newer) || event.nativeEvent.isComposing) return;
    event.preventDefault();
    // Enter logo depois de digitar: busca já, sem esperar a pausa.
    if (enter && term !== requested.current) runSearch(query);
    else step(older ? 1 : -1);
  };

  let counter = null;
  if (longEnough && failed) {
    counter = <span>Enter tenta de novo</span>;
  } else if (longEnough && total > 0) {
    counter = (
      <>
        <span className={classes.counterNow}>{(position + 1).toLocaleString("pt-BR")}</span>{" "}
        <span>
          de {total.toLocaleString("pt-BR")}
          {truncated ? "+" : ""}
        </span>
      </>
    );
  } else if (longEnough && waiting) {
    counter = <CircularProgress size={12} thickness={5} color="inherit" aria-label="Buscando" />;
  } else if (longEnough) {
    counter = <span>Nenhum resultado</span>;
  }

  let barClass = classes.bar;
  if (reduced) barClass += ` ${classes.barStill}`;
  else if (leaving) barClass += ` ${classes.barLeaving}`;

  return (
    <div className={barClass} ref={barRef} role="search">
      <div className={classes.field}>
        <Search size={15} className={classes.fieldIcon} aria-hidden="true" />
        <input
          ref={inputRef}
          className={classes.input}
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Buscar nesta conversa"
          aria-label="Buscar nesta conversa"
          autoComplete="off"
          spellCheck={false}
          maxLength={100}
        />
        <span
          className={`${classes.counter} ${waiting && total > 0 ? classes.counterStale : ""}`}
          aria-live="polite"
          data-search-counter
        >
          {counter}
        </span>
      </div>
      <Tooltip title="Mais antiga (Enter)">
        <span>
          <button
            type="button"
            className={classes.button}
            onClick={() => step(1)}
            disabled={!total || position >= total - 1}
            aria-label="Ocorrência mais antiga"
          >
            <ChevronUp size={18} />
          </button>
        </span>
      </Tooltip>
      <Tooltip title="Mais recente (Shift+Enter)">
        <span>
          <button
            type="button"
            className={classes.button}
            onClick={() => step(-1)}
            disabled={!total || position <= 0}
            aria-label="Ocorrência mais recente"
          >
            <ChevronDown size={18} />
          </button>
        </span>
      </Tooltip>
      <span className={classes.divider} aria-hidden="true" />
      <Tooltip title="Fechar busca (Esc)">
        <button type="button" className={classes.button} onClick={onClose} aria-label="Fechar busca">
          <X size={16} />
        </button>
      </Tooltip>
      {jumping && (
        <div className={classes.progress} aria-hidden="true">
          <div className={`${classes.progressBar} ${reduced ? classes.progressStill : ""}`} />
        </div>
      )}
    </div>
  );
});

/**
 * @param {object} props
 * @param {boolean} props.open Barra aberta.
 * @param {number} props.channelId Conversa em que a busca acontece.
 * @param {{current: HTMLElement}} props.listRef A lista de mensagens (quem rola).
 * @param {(messageId: number) => Promise<boolean>} props.onJump Garante a mensagem na
 *   tela (carrega o trecho dela se for antiga) e resolve depois de desenhada.
 * @param {() => void} props.onClose
 * @param {(ms: number) => void} props.holdPaging Avisa que a próxima rolagem é da
 *   busca, para a lista não carregar páginas no meio do caminho.
 */
const ConversationSearch = forwardRef(({ open, ...props }, ref) => {
  // A barra continua montada por um instante depois de fechar, para sair animada.
  const [mounted, setMounted] = useState(open);
  const [leaving, setLeaving] = useState(false);
  const bar = useRef(null);
  const isMounted = useRef(mounted);
  isMounted.current = mounted;

  useImperativeHandle(
    ref,
    () => ({
      focus: () => bar.current && bar.current.focus(),
      refresh: () => bar.current && bar.current.refresh(),
    }),
    []
  );

  useEffect(() => {
    if (open) {
      setMounted(true);
      setLeaving(false);
      return undefined;
    }
    if (!isMounted.current) return undefined;
    if (prefersReducedMotion()) {
      setMounted(false);
      return undefined;
    }
    setLeaving(true);
    const timer = setTimeout(() => {
      setMounted(false);
      setLeaving(false);
    }, LEAVE_MS);
    return () => clearTimeout(timer);
  }, [open]);

  if (!mounted) return null;
  return <SearchBar ref={bar} leaving={leaving} {...props} />;
});

export default memo(ConversationSearch);
